/**
 * 📐 `SoundTypeEnum` → the `.ogg` the original plays for it.
 *
 * Recovered by **elimination**, because the binding itself is gone.
 * `SoundService` holds a `SoundLibrary` (`dump.cs`:712694), a `ScriptableObject`
 * whose `[SerializeField] List<SoundData> sounds` is the enum-to-clip table —
 * and `MonoBehaviour/SoundLibrary.asset` is a **14-line stub**, the same shape
 * as the TMP FontAsset that blocks IC-3. Nothing in the export says which clip
 * is which.
 *
 * What is left to work with is a coincidence worth taking seriously: the enum
 * has **37** values and `Assets/AudioClip/` holds **37** `.ogg` files. A 1:1
 * assignment exists and is almost entirely forced.
 *
 *  - **29 by exact name.** `LevelFail`, `Confetti`, `ButtonClick`, `Unlock`,
 *    `Jump0..4`, `HiddenReveal`, `IceBreak`, `BusComplete`, `BusExiting`,
 *    `BreakGlass`, `SafeBreak`, `SafeCoinBreak`, `PropellerFly`, `CurtainOpen`,
 *    `CurtainChainBreak`, `BigCubeDestroy`, `CapacityBooster`,
 *    `HardLevelSound`, `SuccessPanel`, `CoinHit`, `CoinEarned`, `HeartHit`,
 *    `GlassBreak`, `RopeCut`, `WrongBallHit`.
 *  - **6 by near-name or numbering.** `CapacityKeepPlaying` →
 *    `Capacity_KeepPlaying`; `BusClick` → `ClickBus` (the two words swapped);
 *    `IceCountdown` → `IceCountReduce`; `BusHit1/2/3` → `BusCrash01/02/03`,
 *    three against three in order.
 *  - **2 by meaning, and then forced by elimination.**
 *    `WrongClickFeedback` → `ShakeBus`: that branch is
 *    `BusMovementHandler.HandleBoxCantMove`, which **shakes the bus** — the
 *    sound is named after what the player sees. `BusMove` → `BusEngineStart`,
 *    the only clip left, and the only one about a bus setting off.
 *
 * ❓ **What the elimination cannot give**, all of it in the stripped `SoundData`:
 * per-clip volume, pitch, whether a clip loops, and whether `Play` picks one of
 * a set. Those need the original bundle.
 *
 * ⚠️ **This is a table, not a player.** The port has no audio layer at all —
 * `session.ts` only mentions `Play(...)` in comments. Wiring one is IC-7b; this
 * is what it will need, and freezing it now means the recovery is checked
 * rather than re-derived from memory later.
 */
import { SoundTypeEnum } from '../core/enums'

export const SOUND_CLIPS: Readonly<Record<SoundTypeEnum, string>> = {
  [SoundTypeEnum.LevelFail]: 'LevelFail',
  [SoundTypeEnum.Confetti]: 'Confetti',
  [SoundTypeEnum.ButtonClick]: 'ButtonClick',
  [SoundTypeEnum.Unlock]: 'Unlock',
  [SoundTypeEnum.Jump0]: 'Jump0',
  [SoundTypeEnum.Jump1]: 'Jump1',
  [SoundTypeEnum.Jump2]: 'Jump2',
  [SoundTypeEnum.Jump3]: 'Jump3',
  [SoundTypeEnum.Jump4]: 'Jump4',
  [SoundTypeEnum.BusClick]: 'ClickBus',
  [SoundTypeEnum.WrongClickFeedback]: 'ShakeBus',
  [SoundTypeEnum.HiddenReveal]: 'HiddenReveal',
  [SoundTypeEnum.IceCountdown]: 'IceCountReduce',
  [SoundTypeEnum.IceBreak]: 'IceBreak',
  [SoundTypeEnum.BusHit1]: 'BusCrash01',
  [SoundTypeEnum.BusHit2]: 'BusCrash02',
  [SoundTypeEnum.BusHit3]: 'BusCrash03',
  [SoundTypeEnum.BusMove]: 'BusEngineStart',
  [SoundTypeEnum.BusComplete]: 'BusComplete',
  [SoundTypeEnum.BusExiting]: 'BusExiting',
  [SoundTypeEnum.BreakGlass]: 'BreakGlass',
  [SoundTypeEnum.SafeBreak]: 'SafeBreak',
  [SoundTypeEnum.SafeCoinBreak]: 'SafeCoinBreak',
  [SoundTypeEnum.PropellerFly]: 'PropellerFly',
  [SoundTypeEnum.CurtainOpen]: 'CurtainOpen',
  [SoundTypeEnum.CurtainChainBreak]: 'CurtainChainBreak',
  [SoundTypeEnum.BigCubeDestroy]: 'BigCubeDestroy',
  [SoundTypeEnum.CapacityBooster]: 'CapacityBooster',
  [SoundTypeEnum.HardLevelSound]: 'HardLevelSound',
  [SoundTypeEnum.SuccessPanel]: 'SuccessPanel',
  [SoundTypeEnum.CoinHit]: 'CoinHit',
  [SoundTypeEnum.CoinEarned]: 'CoinEarned',
  [SoundTypeEnum.HeartHit]: 'HeartHit',
  [SoundTypeEnum.CapacityKeepPlaying]: 'Capacity_KeepPlaying',
  [SoundTypeEnum.GlassBreak]: 'GlassBreak',
  [SoundTypeEnum.RopeCut]: 'RopeCut',
  [SoundTypeEnum.WrongBallHit]: 'WrongBallHit',
}

/**
 * The three the gameplay scene reaches for, by the literal in the decompile.
 *
 * `Bus.DecreaseIceCount`:45-83 plays `0xc`, `Bus.BreakIce`:91-95 plays `0xd`,
 * and the blocked-click branch plays `10` (`session.ts`, `HandleBoxCantMove`).
 * Named here so the literals in those comments have somewhere to resolve to.
 */
export const ICE_COUNTDOWN_SOUND = SoundTypeEnum.IceCountdown
export const ICE_BREAK_SOUND = SoundTypeEnum.IceBreak
export const WRONG_CLICK_SOUND = SoundTypeEnum.WrongClickFeedback

/**
 * 🔍 The two `Bus.PlayDepartEffects` (`__0x20fb9c8.c`) plays, and the gap
 * between them.
 *
 *     _soundService.Play(0x12);            // 18 = BusComplete, line 40
 *     PlayExitingSound().Forget();         // lines 46-57
 *
 * and the UniTaskVoid it forgets is `Bus.<PlayExitingSound>d__100.MoveNext`
 * (`__0x20fe6a0.c`):
 *
 *     await UniTask.Delay(0x96);           // 150 ms, line 45
 *     _soundService.Play(0x13);            // 19 = BusExiting, line 128
 *
 * The delay is real and audible — the two are a one-two, not a chord. It is
 * `UniTask.Delay` with no `ignoreTimeScale`, so it runs on scaled time and
 * stretches with `TimeScaleChangedEvent(1.75)`.
 */
export const BUS_COMPLETE_SOUND = SoundTypeEnum.BusComplete
export const BUS_EXITING_SOUND = SoundTypeEnum.BusExiting
/** `UniTask.Delay(0x96)` — 150 ms, in seconds. */
export const BUS_EXITING_DELAY = 0.15

/**
 * 🔍 `BusMovementHandler.MoveBoxToTarget` (`__0x210a800.c`):149 — `Play(9)`.
 *
 *     if (_boxTransform != null) {
 *         ... clamp the target back by `crashStopDistance + overhang` ...
 *         _soundService?.Play(9);          // BusClick
 *         Taptic.Medium();
 *         _moveSequence?.Kill(true);
 *         _moveSequence = DOTween.Sequence();
 *         ... DOMove(target, |target - position| / 24.64f) ...
 *     }
 *
 * So `ClickBus.ogg` is not the click at all — it is the bus **setting off**,
 * and it fires on both of the two paths that start a lot move:
 * `TryMoveToConveyor` (which inlines `MoveBoxToTarget`, hence the second
 * identical `Play(9)` at `__0x210a4cc.c`:190) and `HandleClicked`:75, the lunge
 * at a blocker. A refused click plays nothing here.
 */
export const BUS_MOVE_SOUND = SoundTypeEnum.BusClick

/**
 * 🔍 `Bus.PlayRevealMaterialTween` (`__0x20f94ec.c`):43 — `Play(0xb)`, the very
 * first statement, ahead of the renderer walk that dissolves the question mark
 * away. Inlined into `Bus.ExitHiddenState` (`__0x20f91bc.c`:153, under the
 * label `Bus__PlayRevealMaterialTween_plus_0x504`), so the two sites are one
 * sound.
 */
export const HIDDEN_REVEAL_SOUND = SoundTypeEnum.HiddenReveal

/**
 * 🔍 `GameplayController.OnLevelFailed` (`__0x211d880.c`):87 — `Play(0)`.
 *
 * Its place in the sequence is readable and worth keeping: `TimeScaleChangedEvent(1)`
 * → `BusController.ToggleInput(false)` → the two `SplineController` flags go to
 * zero → **`Play(LevelFail)`** → `PlayFailCrashAnimation(...)` → the banner.
 * The sound leads the crash, it does not follow it.
 */
export const LEVEL_FAIL_SOUND = SoundTypeEnum.LevelFail

/**
 * 🔍 `QueueController..ctor` (`__0x2123adc.c`):36-42 — `_soundPattern` (0x90).
 *
 *     lVar2 = new SoundTypeEnum[2];
 *     lVar2[0] = 4;                  // Jump0
 *     lVar2[1] = 5;                  // Jump1
 *     _soundPattern = lVar2;
 *
 * Written element by element rather than through `InitializeArray`, so both
 * values are literals in the body — no metadata blob to recover. **Two**
 * entries, not the five `Jump0..Jump4` the clip names suggest: `Jump2`, `Jump3`
 * and `Jump4` exist in `Assets/AudioClip/` and this build never reaches them.
 */
export const BOARDING_JUMP_SOUND_PATTERN: readonly SoundTypeEnum[] = [
  SoundTypeEnum.Jump0,
  SoundTypeEnum.Jump1,
]

/**
 * 📐 `BusMovementHandler.ShakeObject` (`__0x210aea4.c`):47-64 — the crash half.
 *
 *     ShakeObject(Action onComplete, bool playRandomCrashSound) {
 *         Taptic.Heavy();
 *         if (playRandomCrashSound) {
 *             var pool = new SoundTypeEnum[3];              // FUN_00ab1834(..., 3)
 *             RuntimeHelpers.InitializeArray(pool, <blob>);
 *             _soundService?.Play(pool[Random.Range(0, pool.Length)]);
 *         } else {
 *             _soundService?.Play(10);                      // WrongClickFeedback
 *         }
 *         ... DOPunchRotation((1,3,3), 0.2f, 10, 1f) ...
 *     }
 *
 * ⚠️ **The three values are the one thing here that is not read.** Unlike
 * `_soundPattern` above, this array is filled by `RuntimeHelpers.InitializeArray`
 * from a metadata blob, and the blob is not in the export — the same wall that
 * `SoundLibrary.asset` puts up. What pins it is the same elimination
 * `soundLibrary`'s table rests on: the enum holds exactly one run of three
 * numbered alternatives for a bus impact (`BusHit1`, `BusHit2`, `BusHit3` =
 * 14, 15, 16), `Assets/AudioClip/` holds exactly three `BusCrash0N.ogg`, and
 * nothing else in the enum plays in this scene. To settle it: the original
 * bundle.
 *
 * Only **one** of the four `ShakeObject` call sites passes `true` —
 * `<HandleClicked>b__2`:50, the blocker's jolt. The bus that did the ramming
 * shakes with `false` (via `MoveAfterCollision`:35), so a collision is one
 * crash clip and one `ShakeBus`, not two crash clips.
 */
export const CRASH_SOUND_POOL: readonly SoundTypeEnum[] = [
  SoundTypeEnum.BusHit1,
  SoundTypeEnum.BusHit2,
  SoundTypeEnum.BusHit3,
]

/**
 * 🔍 `SuccessAnimations.Show` (`__0x21407a8.c`):77, 104-110 — the win panel.
 *
 *     foreach (var p in _confettiParticles) { p.Play(); _soundService?.Play(1); }
 *     ...
 *     _soundService.Build(0x1d).WithDelay(1.4f).Play();     // SuccessPanel
 *
 * `Play(1)` sits **inside** the confetti loop, so it fires once per
 * `ParticleImage` in the array. That is not observable: `CanPlaySound` is keyed
 * by `SoundTypeEnum` and the whole loop runs inside one frame, so every call
 * after the first is refused by the cooldown. The port plays it once and says
 * so rather than inventing a particle count.
 *
 * The second is the only `SoundBuilder` in reach of the gameplay flow, and the
 * only thing it overrides is the delay: `0x3fb33333` = 1.4 s.
 */
export const CONFETTI_SOUND = SoundTypeEnum.Confetti
export const SUCCESS_PANEL_SOUND = SoundTypeEnum.SuccessPanel
/** `WithDelay(0x3fb33333)` — 1.4 s, in seconds. */
export const SUCCESS_PANEL_DELAY = 1.4
